// Extracted verbatim from original/src/components/ThemeStudioModal.tsx (ADR-08 §2.1; chat C30, O-10, D-285).
// Cut: saveTheme() to localStorage — the choice is persisted by the container through setActiveTheme as the
// personal preference `ui.theme_studio` (D-123); display scale and icon size likewise (`ui.display_scale`,
// `ui.icon_size`). Everything else, including the image colour extraction, is the old code.
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Palette, Upload, Check, RefreshCw, Sparkles, Sun, Moon, Image as ImageIcon, CheckCircle2, Monitor } from 'lucide-react';
import {
  type CustomAppTheme,
  PRESET_THEMES,
  extractColorsFromImageFile,
  applyThemeToDocument,
} from '../lib/theme.js';

interface ThemeStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeTheme: CustomAppTheme;
  setActiveTheme: (theme: CustomAppTheme) => void;
  darkMode: boolean;
  setDarkMode: (val: boolean) => void;
  lang: string;
  displayResolution: number;
  setDisplayResolution: (val: number) => void;
  dashboardIconSize: 'sm' | 'md' | 'lg' | 'xl';
  setDashboardIconSize: (val: 'sm' | 'md' | 'lg' | 'xl') => void;
}

export const ThemeStudioModal: React.FC<ThemeStudioModalProps> = ({
  isOpen,
  onClose,
  activeTheme,
  setActiveTheme,
  darkMode,
  setDarkMode,
  lang,
  displayResolution,
  setDisplayResolution,
  dashboardIconSize,
  setDashboardIconSize
}) => {
  const [selectedTheme, setSelectedTheme] = useState<CustomAppTheme>(activeTheme);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedTheme, setExtractedTheme] = useState<CustomAppTheme | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleSelectPreset = (theme: CustomAppTheme) => {
    setSelectedTheme(theme);
    applyThemeToDocument(theme);
    setActiveTheme(theme);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setExtractError(lang === 'ar' ? 'يرجى اختيار ملف صورة صالح (PNG, JPG, WebP)' : 'Please select a valid image file');
      return;
    }

    try {
      setIsExtracting(true);
      setExtractError(null);
      const customTheme = await extractColorsFromImageFile(file);
      setExtractedTheme(customTheme);
      setSelectedTheme(customTheme);
      applyThemeToDocument(customTheme);
      setActiveTheme(customTheme);
    } catch (err) {
      console.error(err);
      setExtractError(lang === 'ar' ? 'حدث خطأ أثناء تحليل ألوان الصورة' : 'Failed to extract colors from image');
    } finally {
      setIsExtracting(false);
    }
  };

  const isAr = lang === 'ar';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
        />

        {/* Modal Container */}
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 15 }}
          className="relative z-10 w-full max-w-2xl bg-white dark:bg-[#0e131f] rounded-3xl shadow-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800/80 flex items-center justify-between bg-slate-50/80 dark:bg-[#131a29]/80 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-primary-500 to-amber-500 flex items-center justify-center text-white shadow-lg shadow-primary-500/20">
                <Palette size={20} />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <span>{isAr ? 'استوديو الثيمات والألوان المحترف' : 'Professional Theme Studio'}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-400 font-bold border border-primary-500/20">
                    Pro
                  </span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  {isAr ? 'خصص المظهر الكامل للتطبيق أو استخرج الألوان تلقائياً من صورتك أو شعارك' : 'Customize app visual identity or extract colors automatically from image'}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar">
            {/* Quick Mode Switch & Current Theme Preview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Light/Dark Toggle */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-[#151c2c] border border-slate-200/70 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <span className="block text-xs font-black text-slate-900 dark:text-white mb-0.5">
                    {isAr ? 'نمط المظهر (الوضع)' : 'Color Scheme Mode'}
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400 font-bold">
                    {darkMode ? (isAr ? 'الوضع المظلم الفاخر' : 'Luxury Dark Mode') : (isAr ? 'الوضع النهاري المشرق' : 'Bright Light Mode')}
                  </span>
                </div>

                <div className="flex gap-1 bg-white dark:bg-[#0a0e17] p-1 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-inner">
                  <button
                    onClick={() => setDarkMode(false)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                      !darkMode
                        ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
                    }`}
                  >
                    <Sun size={14} />
                    <span>{isAr ? 'نهاري' : 'Light'}</span>
                  </button>

                  <button
                    onClick={() => setDarkMode(true)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                      darkMode
                        ? 'bg-primary-500 text-white shadow-md shadow-primary-500/20'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
                    }`}
                  >
                    <Moon size={14} />
                    <span>{isAr ? 'مظلم' : 'Dark'}</span>
                  </button>
                </div>
              </div>

              {/* Active Theme Badge */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-[#151c2c] border border-slate-200/70 dark:border-slate-800 flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-md shrink-0"
                  style={{ background: `linear-gradient(135deg, ${selectedTheme.primary}, ${selectedTheme.accent})` }}
                >
                  <Sparkles size={22} />
                </div>
                <div className="space-y-0.5 min-w-0">
                  <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                    {isAr ? 'الثيم النشط حالياً' : 'Active Theme'}
                  </span>
                  <h4 className="text-sm font-black text-slate-900 dark:text-white truncate">
                    {isAr ? selectedTheme.nameAr : selectedTheme.name}
                  </h4>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border border-white dark:border-slate-900 shadow-sm" style={{ backgroundColor: selectedTheme.primary }} />
                    <span className="w-3 h-3 rounded-full border border-white dark:border-slate-900 shadow-sm" style={{ backgroundColor: selectedTheme.accent }} />
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono font-bold mr-1">
                      {selectedTheme.primary}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Screen Scale and Icon Size controls */}
            <div className="p-5 rounded-2xl bg-slate-50 dark:bg-[#151c2c] border border-slate-200/70 dark:border-slate-800 space-y-5">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Monitor size={16} className="text-primary-500" />
                <span>{isAr ? 'إعدادات دقة العرض وتكبير واجهات النظام' : 'Display Resolution & Layout Controls'}</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 1. Display Resolution Slider / Preset Controls */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-black text-slate-900 dark:text-white flex items-center gap-1.5">
                      <span>{isAr ? 'دقة العرض (قياس الشاشة):' : 'Display Resolution (Scale):'}</span>
                      <span className="text-xs font-mono font-black text-primary-500 bg-primary-500/10 px-2 py-0.5 rounded-lg">
                        {displayResolution}%
                      </span>
                    </label>
                  </div>
                  
                  {/* Resolution Slider */}
                  <div className="space-y-2">
                    <input
                      type="range"
                      min="85"
                      max="130"
                      step="5"
                      value={displayResolution}
                      onChange={(e) => setDisplayResolution(parseInt(e.target.value))}
                      className="w-full accent-primary-500 h-2 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                    
                    {/* Quick Presets */}
                    <div className="flex justify-between text-[10px] font-bold text-slate-400 dark:text-slate-500">
                      <button 
                        type="button" 
                        onClick={() => setDisplayResolution(90)}
                        className={`hover:text-primary-500 transition-colors ${displayResolution === 90 ? 'text-primary-500 font-black' : ''}`}
                      >
                        90% ({isAr ? 'مدمج' : 'Compact'})
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setDisplayResolution(100)}
                        className={`hover:text-primary-500 transition-colors ${displayResolution === 100 ? 'text-primary-500 font-black' : ''}`}
                      >
                        100% ({isAr ? 'افتراضي' : 'Default'})
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setDisplayResolution(110)}
                        className={`hover:text-primary-500 transition-colors ${displayResolution === 110 ? 'text-primary-500 font-black' : ''}`}
                      >
                        110% ({isAr ? 'كبير' : 'Large'})
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setDisplayResolution(120)}
                        className={`hover:text-primary-500 transition-colors ${displayResolution === 120 ? 'text-primary-500 font-black' : ''}`}
                      >
                        120% ({isAr ? 'أكبر' : 'Larger'})
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-relaxed">
                    {isAr 
                      ? 'يساعدك هذا في زيادة أو تقليل حجم واجهات التطبيق بالكامل لتلائم أبعاد شاشتك وتمنع أي اقتصاص.' 
                      : 'Adjust the entire application scale up or down to match your monitor and prevent content clipping.'}
                  </p>
                </div>

                {/* 2. Control Panel Icon Sizes */}
                <div className="space-y-3">
                  <label className="text-xs font-black text-slate-900 dark:text-white block">
                    {isAr ? 'حجم أيقونات لوحة التحكم:' : 'Control Panel Icon Size:'}
                  </label>

                  <div className="grid grid-cols-4 gap-1.5 bg-white dark:bg-[#0a0e17] p-1 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-inner">
                    {(['sm', 'md', 'lg', 'xl'] as const).map((sz) => {
                      const isActive = dashboardIconSize === sz;
                      const sizeLabels: Record<string, { ar: string; en: string }> = {
                        sm: { ar: 'صغير', en: 'Small' },
                        md: { ar: 'متوسط', en: 'Medium' },
                        lg: { ar: 'كبير', en: 'Large' },
                        xl: { ar: 'ضخم', en: 'Huge' }
                      };
                      return (
                        <button
                          key={sz}
                          type="button"
                          onClick={() => setDashboardIconSize(sz)}
                          className={`py-1.5 px-2 rounded-lg text-[11px] font-bold transition-all text-center ${
                            isActive
                              ? 'bg-primary-500 text-white shadow-md shadow-primary-500/20 font-black'
                              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
                          }`}
                        >
                          {isAr ? sizeLabels[sz]!.ar : sizeLabels[sz]!.en}
                        </button>
                      );
                    })}
                  </div>

                  <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-relaxed">
                    {isAr 
                      ? 'التحكم المباشر بحجم الأيقونات والرموز التوضيحية داخل بطاقات الإحصائيات وأزرار الإجراءات السريعة.' 
                      : 'Directly control the size of icons inside stat cards and action widgets on your dashboard.'}
                  </p>
                </div>
              </div>
            </div>

            {/* AI Image Color Extraction Section */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-primary-500/5 via-amber-500/5 to-transparent border border-primary-500/20 dark:border-primary-500/10 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <ImageIcon size={18} className="text-primary-500" />
                    <h3 className="text-sm font-black text-slate-900 dark:text-white">
                      {isAr ? 'استخراج ثيم متكامل تلقائياً من صورة' : 'Extract Custom Theme from Image'}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    {isAr 
                      ? 'ارفع شعار مؤسستك أو صورة أو خلفية مفضلة، وسيقوم المحرك الذكي بتحليل درجات الألوان وإنشاء ثيم احترافي متناسق لكل واجهات التطبيق' 
                      : 'Upload a logo, banner or photo. Our engine will extract dominant colors and apply a matching theme everywhere'}
                  </p>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />

                <button
                  type="button"
                  disabled={isExtracting}
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-indigo-600 hover:from-primary-600 hover:to-indigo-700 text-white text-xs font-black shadow-lg shadow-primary-500/25 transition-all flex items-center gap-2 shrink-0 cursor-pointer disabled:opacity-50 active:scale-95"
                >
                  {isExtracting ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      <span>{isAr ? 'جاري التحليل...' : 'Analyzing...'}</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>{isAr ? 'رفع صورة' : 'Upload Image'}</span>
                    </>
                  )}
                </button>
              </div>

              {extractError && (
                <p className="text-xs font-bold text-rose-500 bg-rose-50 dark:bg-rose-950/30 p-2.5 rounded-xl border border-rose-200 dark:border-rose-900/50">
                  {extractError}
                </p>
              )}

              {/* Extracted Theme Result Card */}
              {extractedTheme && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-white dark:bg-[#131926] rounded-2xl border border-primary-500/30 shadow-md space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                      <CheckCircle2 size={15} />
                      <span>{isAr ? 'تم استخراج الثيم بنجاح وتطبيقه!' : 'Custom Theme Extracted Successfully!'}</span>
                    </span>

                    {extractedTheme.imageUrl && (
                      <img 
                        src={extractedTheme.imageUrl} 
                        alt="Uploaded Preview" 
                        className="w-10 h-10 object-cover rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm"
                      />
                    )}
                  </div>

                  {/* Swatches */}
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      {isAr ? 'لوحة الألوان المستخرجة:' : 'Extracted Palette:'}
                    </span>
                    {extractedTheme.palette?.map((c, i) => (
                      <div key={i} className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800/80 px-2 py-1 rounded-lg border border-slate-200/50 dark:border-slate-700">
                        <span className="w-3.5 h-3.5 rounded-full shadow-sm" style={{ backgroundColor: c }} />
                        <span className="text-[10px] font-mono font-bold text-slate-600 dark:text-slate-300">{c}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Presets Grid */}
            <div className="space-y-3">
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">
                {isAr ? 'ثيمات جاهزة متناسقة' : 'Curated Preset Themes'}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PRESET_THEMES.map((theme) => {
                  const isSelected = selectedTheme.id === theme.id && !selectedTheme.isCustomImage;
                  return (
                    <button
                      key={theme.id}
                      onClick={() => handleSelectPreset(theme)}
                      className={`p-3.5 rounded-2xl border transition-all text-right flex items-center justify-between gap-3 cursor-pointer group ${
                        isSelected
                          ? 'bg-primary-500/10 border-primary-500 shadow-lg shadow-primary-500/10 ring-2 ring-primary-500/30'
                          : 'bg-slate-50 dark:bg-[#151c2c] border-slate-200/80 dark:border-slate-800 hover:border-primary-500/40 hover:bg-slate-100 dark:hover:bg-[#1a2336]'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-md shrink-0 transition-transform group-hover:scale-105"
                          style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}
                        >
                          {isSelected ? <Check size={18} /> : <Palette size={16} className="opacity-70" />}
                        </div>

                        <div className="min-w-0">
                          <span className="block text-xs font-black text-slate-900 dark:text-white truncate">
                            {isAr ? theme.nameAr : theme.name}
                          </span>
                          <div className="flex items-center gap-1.5 mt-1">
                            {theme.palette?.slice(0, 4).map((col, idx) => (
                              <span key={idx} className="w-3 h-3 rounded-full border border-white/50 shadow-xs" style={{ backgroundColor: col }} />
                            ))}
                          </div>
                        </div>
                      </div>

                      {isSelected && (
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-primary-500 text-white shrink-0">
                          {isAr ? 'نشط' : 'Active'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Live Component Preview Box */}
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-[#131926] border border-slate-200/80 dark:border-slate-800 space-y-3">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                {isAr ? 'معاينة حية لمكونات الواجهة بالتصميم المختار:' : 'Live Component Preview:'}
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button type="button" className="btn-primary w-full text-center text-xs py-2.5">
                  {isAr ? 'زر رئيسي محدد' : 'Primary Button'}
                </button>

                <div className="nav-item-active px-3 py-2 rounded-xl text-xs font-bold text-center flex items-center justify-center gap-1">
                  <Sparkles size={14} />
                  <span>{isAr ? 'عنصر قراءة نشط' : 'Active Navigation'}</span>
                </div>

                <div className="input-premium text-xs text-slate-500 dark:text-slate-400 font-mono text-center">
                  {isAr ? 'حقل إدخال بتركيز متناسق' : 'Styled Form Field'}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/80 dark:bg-[#131a29]/80 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => handleSelectPreset(PRESET_THEMES[0]!)}
              className="text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors flex items-center gap-1"
            >
              <RefreshCw size={14} />
              <span>{isAr ? 'استعادة الثيم الافتراضي' : 'Reset to Default'}</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="btn-primary text-xs px-6 py-2.5"
            >
              {isAr ? 'إغلاق وتطبيق' : 'Done'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
